import { isStaleAccountError } from '@/lib/stale-account';
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  MessageSquare,
  MessageCircle,
  CheckCheck,
  Trash2,
} from "lucide-react";
import messagesService, { type Conversation } from "@/lib/services/messages/messages.service";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { services } from "@/lib/services";
import {
  formatNameWithInitial,
  professionalFullDisplayName,
  professionalShortDisplayName,
} from "@/lib/utils/user-display-name";

// Helper function to format name with truncated last name
const formatDisplayName = (name: string): string => {
  if (!name || !name.trim()) return 'Professional';

  const nameParts = name.trim().split(' ');
  if (nameParts.length === 1) {
    return name;
  }

  // Take all parts except last, then first letter of last part + "."
  const allButLast = nameParts.slice(0, -1);
  const lastInitial = nameParts[nameParts.length - 1][0];
  return `${allButLast.join(' ')} ${lastInitial}.`;
};

const getFirstInitial = (name?: string): string => {
  const first = (name || "").trim();
  return first ? first[0].toUpperCase() : "U";
};

export function MessagesDropdown() {
  const { user } = useFirebaseAuth();
  const confirm = useConfirm();
  const [location, setLocation] = useLocation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [participantProfiles, setParticipantProfiles] = useState<{[userId: string]: {profileImageUrl?: string, displayName?: string, firstInitial?: string}}>({});

  // Real-time subscription to conversations
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setConversations([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    // Answers that arrive after this account is gone (signed out, or
    // another account signed in) are dropped.
    let active = true;

    // Timeout fallback - stop loading after 5 seconds and try static method
    const timeoutId = setTimeout(async () => {
      console.warn('⚠️ Messages loading timeout - trying static method');
      try {
        const staticConversations = await messagesService.getUserConversations(user.id);
        if (!active) return;
        setConversations(staticConversations);
        const totalUnread = staticConversations.reduce((sum, conv) =>
          sum + (conv.unreadCount[user.id] || 0), 0
        );
        setUnreadCount(totalUnread);
      } catch (error) {
        if (!active || isStaleAccountError(error)) return;
        console.error('❌ Static conversations fetch failed:', error);
        toast.error('Failed to load messages');
      }
      setLoading(false);
    }, 5000);

    const unsubscribe = messagesService.subscribeToConversations(
      user.id,
      (newConversations) => {
        console.log('💬 Conversations received:', newConversations.length, newConversations);
        clearTimeout(timeoutId); // Clear timeout since we got data
        setConversations(newConversations);
        const totalUnread = newConversations.reduce((sum, conv) =>
          sum + (conv.unreadCount[user.id] || 0), 0
        );
        setUnreadCount(totalUnread);
        setLoading(false);
      }
    );

    // Cleanup subscription and timeout on unmount
    return () => {
      active = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [user?.id]);

  // Load profile data for all participants
  useEffect(() => {
    const loadParticipantProfiles = async () => {
      const allParticipantIds = new Set<string>();
      conversations.forEach(conv => {
        conv.participants.forEach(id => {
          if (id !== user?.id) {
            allParticipantIds.add(id);
          }
        });
      });

      const profilePromises = Array.from(allParticipantIds).map(async (userId) => {
        try {
          // Try to get professional profile first, then fall back to user data
          const [professionalProfile, userData] = await Promise.all([
            services.professionalProfile.getUserProfessionalProfile(userId).catch(() => null),
            services.admin.getUserById(userId).catch(() => null)
          ]);

          // Filter out blob URLs since they're invalid across sessions
          const professionalImageUrl = professionalProfile?.profileImageUrl && !professionalProfile.profileImageUrl.startsWith('blob:')
            ? professionalProfile.profileImageUrl
            : null;
          const fromUser =
            userData?.firstName && userData?.lastName
              ? formatNameWithInitial(userData.firstName, userData.lastName)
              : userData?.firstName || "";
          const displayName =
            professionalShortDisplayName(professionalProfile) ||
            formatDisplayName(professionalFullDisplayName(professionalProfile)) ||
            formatDisplayName(fromUser) ||
            "Professional";

          return {
            userId,
            profileImageUrl: professionalImageUrl || undefined,
            displayName,
            firstInitial: getFirstInitial(userData?.firstName || professionalProfile?.firstName || displayName),
          };
        } catch (error) {
          console.error(`Error loading profile for user ${userId}:`, error);
          return { userId, profileImageUrl: undefined, displayName: undefined, firstInitial: "U" };
        }
      });

      const profiles = await Promise.all(profilePromises);
      const profileMap = profiles.reduce((acc, profile) => {
        acc[profile.userId] = {
          profileImageUrl: profile.profileImageUrl,
          displayName: profile.displayName
        };
        return acc;
      }, {} as {[userId: string]: {profileImageUrl?: string, displayName?: string, firstInitial?: string}});

      setParticipantProfiles(profileMap);
    };

    if (conversations.length > 0 && user?.id) {
      loadParticipantProfiles();
    }
  }, [conversations, user?.id]);

  const handleConversationClick = async (conversation: Conversation) => {
    // Check if we're already on the messages page
    if (location.startsWith('/messages')) {
      // We're on the messages page, update URL parameters to switch conversation
      // This will trigger the URL parameter handler in the messages page
      const newUrl = `/messages?conversation=${conversation.id}`;
      window.history.pushState({}, '', newUrl);

      // Dispatch a custom event to notify the messages page to switch conversations
      window.dispatchEvent(new CustomEvent('switch-conversation', {
        detail: { conversationId: conversation.id }
      }));
    } else {
      // Navigate to messages page with conversation selected
      setLocation(`/messages?conversation=${conversation.id}`);
    }

    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    try {
      // Mark all conversations as read by updating each one individually
      const markPromises = conversations.map(async (conv) => {
        if (conv.unreadCount[user.id] > 0) {
          await messagesService.markMessagesAsRead(conv.id!, user.id);
        }
      });
      await Promise.all(markPromises);

      // Update local state
      setConversations(prev => prev.map(conv => ({
        ...conv,
        unreadCount: { ...conv.unreadCount, [user.id]: 0 }
      })));
      setUnreadCount(0);

      toast.success('All messages marked as read');
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const confirmed = await confirm({
      title: 'Delete conversation?',
      description: 'Are you sure you want to delete this conversation? This action cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    if (!user?.id) {
      toast.error('You must be signed in to delete a conversation');
      return;
    }

    try {
      await messagesService.deleteConversation(conversationId, user.id);
      toast.success('Conversation deleted successfully');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const formatTimeAgo = (timestamp: any) => {
    try {
      let date: Date;

      if (timestamp?.seconds) {
        // Firestore Timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Recently';
      }

      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInDays < 7) return `${diffInDays}d ago`;

      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  const getOtherParticipantName = (conversation: Conversation) => {
    if (!user?.id) return 'Unknown';

    const otherParticipantId = conversation.participants.find(id => id !== user.id);
    if (!otherParticipantId) return 'Unknown';

    // Try enhanced profile data first, then fall back to conversation stored names (with formatting)
    return participantProfiles[otherParticipantId]?.displayName ||
           formatDisplayName(conversation.participantNames[otherParticipantId] || '') ||
           'Unknown User';
  };

  const getOtherParticipantAvatar = (conversation: Conversation) => {
    if (!user?.id) return undefined;

    const otherParticipantId = conversation.participants.find(id => id !== user.id);
    if (!otherParticipantId) return undefined;

    // Avatar images should come from professional profile only.
    return participantProfiles[otherParticipantId]?.profileImageUrl;
  };

  const getOtherParticipantInitial = (conversation: Conversation) => {
    if (!user?.id) return "U";
    const otherParticipantId = conversation.participants.find(id => id !== user.id);
    if (!otherParticipantId) return "U";
    return participantProfiles[otherParticipantId]?.firstInitial ||
      getFirstInitial(participantProfiles[otherParticipantId]?.displayName || conversation.participantNames[otherParticipantId]);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-white hover:text-cyan-400 p-2"
        >
          {unreadCount > 0 ? (
            <MessageCircle className="h-5 w-5" />
          ) : (
            <MessageSquare className="h-5 w-5" />
          )}

          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="max-h-96 w-[calc(100vw-1rem)] max-w-80 overflow-y-auto border-gray-700 bg-gray-800 text-white"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Messages</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs text-cyan-400 hover:text-cyan-300 p-1 h-auto"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-gray-700" />

        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600 mx-auto mb-2"></div>
            <p className="text-gray-400 text-sm">Loading...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-center">
            <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No messages yet</p>
          </div>
        ) : (
          conversations.slice(0, 10).map((conversation) => {
            const otherParticipantName = getOtherParticipantName(conversation);
            const otherParticipantAvatar = getOtherParticipantAvatar(conversation);
            const otherParticipantInitial = getOtherParticipantInitial(conversation);
            const hasUnread = conversation.unreadCount[user?.id || ''] > 0;

            return (
              <DropdownMenuItem
                key={conversation.id}
                className="p-3 cursor-pointer hover:bg-gray-700 focus:bg-gray-700 flex-col items-start"
                onClick={() => handleConversationClick(conversation)}
              >
                <div className="flex items-start gap-3 w-full">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {otherParticipantAvatar ? (
                      <img
                        src={otherParticipantAvatar}
                        alt={otherParticipantName}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          // Hide the broken image and show fallback
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector('.avatar-fallback');
                            if (fallback) (fallback as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="avatar-fallback w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center" style={{ display: otherParticipantAvatar ? 'none' : 'flex' }}>
                      <span className="text-xs font-semibold text-gray-100 uppercase">{otherParticipantInitial}</span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`text-sm font-medium truncate ${hasUnread ? 'text-white' : 'text-gray-300'}`}>
                        {otherParticipantName}
                      </h4>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {hasUnread && (
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                        <span className="text-xs text-gray-400">
                          {formatTimeAgo(conversation.lastMessageTime)}
                        </span>
                        <button
                          onClick={(e) => handleDeleteConversation(conversation.id!, e)}
                          className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors ml-1"
                          title="Delete conversation"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    <p className={`text-xs line-clamp-2 ${hasUnread ? 'text-gray-200' : 'text-gray-400'}`}>
                      {conversation.lastMessage || 'No messages yet'}
                    </p>

                    {/* Job info for job-related conversations */}
                    {conversation.jobTitle && (
                      <div className="mt-1">
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                          Job: {conversation.jobTitle}
                        </Badge>
                      </div>
                    )}

                    {/* Unread count badge */}
                    {hasUnread && (
                      <div className="mt-1">
                        <Badge className="text-xs bg-red-500 text-white">
                          {conversation.unreadCount[user?.id || '']} unread
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })
        )}

        {conversations.length > 10 && (
          <>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem
              className="text-center text-cyan-400 hover:text-cyan-300 cursor-pointer"
              onClick={() => {
                setLocation('/messages');
                setIsOpen(false);
              }}
            >
              View all messages
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
